import React, { useState } from "react";
import { MapPin, Mail, Phone, Send, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { contactData } from "../data/mockData";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    mensagem: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log("Form submitted:", formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ nome: "", email: "", mensagem: "" });
    }, 3000);
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="heading-2 mb-6">Entre em Contato</h2>
          <p className="body-large max-w-3xl mx-auto text-gray-700">
            Tem uma ideia inovadora? Quer ser nosso parceiro? Entre em contato conosco 
            e vamos construir juntos o futuro sustentável da Amazônia
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Address */}
            <div className="innovation-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent-wash rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-accent-text" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Nosso Endereço</h3>
                  <p className="body-medium text-gray-600">
                    {contactData.address.street}<br />
                    {contactData.address.cep}<br />
                    {contactData.address.city}
                  </p>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="innovation-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent-wash rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-accent-text" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Email</h3>
                  <p className="body-medium text-gray-600">{contactData.email}</p>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="innovation-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent-wash rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-accent-text" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Telefone</h3>
                  <p className="body-medium text-gray-600">{contactData.phone}</p>
                </div>
              </div>
            </div>

            {/* Innovation Hub Info 
            <div className="bg-gradient-to-r from-accent-primary to-accent-strong p-6 rounded-2xl text-white">
              <h3 className="heading-3 mb-3 text-white">Hub de Inovação Amazônica</h3>
              <p className="body-medium text-white/90">
                Visite nosso espaço de inovação em Rio Branco e conheça os projetos 
                que estão transformando a Amazônia através da tecnologia e sustentabilidade.
              </p>
            </div>*/}
          </div>

          {/* Contact Form */}
          <div className="innovation-card">
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo
                  </label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    required
                    value={formData.nome}
                    onChange={handleInputChange}
                    placeholder="Seu nome completo"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="seu.email@exemplo.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem
                  </label>
                  <Textarea
                    id="mensagem"
                    name="mensagem"
                    required
                    value={formData.mensagem}
                    onChange={handleInputChange}
                    placeholder="Conte-nos sobre sua ideia, projeto ou como podemos colaborar..."
                    rows={5}
                    className="w-full"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Enviar Mensagem
                </Button>
              </form>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="heading-3 mb-3 text-green-700">Mensagem Enviada!</h3>
                <p className="body-medium text-gray-600">
                  Obrigado pelo seu contato. Nossa equipe responderá em breve.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Innovation Contact CTA */}
        <div className="mt-16 text-center">
          <div className="bg-white p-8 rounded-2xl shadow-sm border-2 border-accent-primary/20">
            <h3 className="heading-3 mb-4">Pronto para Inovar Conosco?</h3>
            <p className="body-medium text-gray-600 mb-6 max-w-2xl mx-auto">
              Seja parte da transformação da Amazônia. Juntos, podemos criar soluções 
              inovadoras para os desafios mais importantes da nossa região.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="btn-primary">
                Agendar Reunião
              </Button>
              <Button variant="outline" className="btn-secondary">
                Conhecer Projetos
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;